import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { authAPI } from "../services/api";
import { formatApiError } from "../lib/errors";
import { cn } from "../lib/utils";
import { Button, Field, InlineNote } from "./ds";
import { AuthHeading, AuthLayout, StatusPanel, SwitchLink } from "./auth/AuthLayout";

// Must match backend ResetPasswordRequest.new_password min_length=10
// in backend/app/models/auth_schemas.py.
const MIN_PASSWORD_LENGTH = 10;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate("/login", { replace: true }), 3000);
    } catch (err) {
      setError(formatApiError(err, "Failed to reset password. The link may be invalid or expired."));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout>
        <StatusPanel icon="x" title="This link has expired"
          body="Reset links work for an hour and only once. Ask for a fresh one.">
          <Button block onClick={() => navigate("/forgot-password")}>Send a new link</Button>
        </StatusPanel>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout>
        <StatusPanel icon="check" title="Password changed" body="Taking you to sign in…">
          <Button block onClick={() => navigate("/login", { replace: true })}>Sign in now</Button>
        </StatusPanel>
      </AuthLayout>
    );
  }

  // The hint under the new password counts towards the minimum as they type.
  const hint = password.length === 0
    ? { text: `Use at least ${MIN_PASSWORD_LENGTH} characters.`, cls: "text-text-tertiary" }
    : password.length >= MIN_PASSWORD_LENGTH
      ? { text: `Looks good — ${password.length} characters.`, cls: "text-accent-default" }
      : { text: `${MIN_PASSWORD_LENGTH - password.length} more character${MIN_PASSWORD_LENGTH - password.length === 1 ? "" : "s"} to go.`, cls: "text-zone-threshold" };

  return (
    <AuthLayout>
      <AuthHeading title="Set a new password" />
      <div className="flex flex-col gap-4">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && <InlineNote tone="error" text={error} />}
          <div className="flex flex-col gap-1.5">
            <Field label="New password" type="password" autoComplete="new-password"
              aria-describedby="reset-password-hint"
              value={password} onChange={(e) => setPassword(e.target.value)} required />
            <p id="reset-password-hint" className={cn("m-0 type-caption", hint.cls)} aria-live="polite">{hint.text}</p>
          </div>
          <Field label="Confirm password" type="password" autoComplete="new-password"
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
          <Button type="submit" block loading={isLoading}>Reset password</Button>
        </form>
        <SwitchLink prompt="Remembered it?" to="/login" label="Sign in" />
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
